import React from "react";
import {
  Box,
  Heading,
  VStack,
  Flex,
  Text,
  Icon,
  Collapse,
  useDisclosure,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import {
  FaChevronDown,
  FaChevronUp,
  FaQuestion,
  FaLightbulb,
  FaFlask,
  FaClock,
  FaGlobe,
  FaTint,
  FaCreditCard,
  FaSprayCan,
  FaTools,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import { texts } from "../../lang";
import { COLORS } from "../../globalvariables";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);

const FAQ = () => {
  const { language } = useLanguage();
  const { faq } = texts[language];

  // Separate the first question and the rest
  const firstQuestion = faq.questions[0];

  // Get the remaining questions and split into two columns
  const remainingQuestions = faq.questions.slice(1);
  const leftColumnQuestions = remainingQuestions.slice(0, 4);
  const rightColumnQuestions = remainingQuestions.slice(4);

  return (
    <Box py={{ base: 8, md: 16 }} px={{ base: 2, md: 4 }} id="faq" zIndex={1}>
      <MotionHeading
        as="h2"
        fontSize={{ base: "1.8rem", md: "2.5rem" }}
        fontWeight="bold"
        textAlign="center"
        mb={{ base: 8, md: 12 }}
        style={{
          color: COLORS.textPrimary,
          backgroundImage: `linear-gradient(to right, ${COLORS.textPrimary}, ${COLORS.primary} 50%, ${COLORS.textPrimary})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          position: "relative",
        }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        {faq.title}
        <Box
          as="div"
          height="3px"
          width="60px"
          bg={COLORS.primary}
          borderRadius="3px"
          margin="8px auto 0"
        />
      </MotionHeading>

      <VStack spacing={{ base: 4, md: 6 }} maxW="1200px" mx="auto">
        {/* First question - centered and full width */}
        <Box w="100%" mx="auto">
          <FAQItem
            question={firstQuestion.question}
            answer={firstQuestion.answer}
            icon={getIconForQuestion(0)}
            delay={0}
            isFirst={true}
          />
        </Box>

        {/* Remaining questions in two columns */}
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          gap={{ base: 4, md: 6 }}
          w="100%"
        >
          <GridItem>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              {leftColumnQuestions.map((item, index) => (
                <FAQItem
                  key={index + 1}
                  question={item.question}
                  answer={item.answer}
                  icon={getIconForQuestion(index + 1)}
                  delay={(index + 1) * 0.1}
                />
              ))}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              {rightColumnQuestions.map((item, index) => (
                <FAQItem
                  key={index + 5}
                  question={item.question}
                  answer={item.answer}
                  icon={getIconForQuestion(index + 5)}
                  delay={(index + 5) * 0.1}
                />
              ))}
            </VStack>
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
};

const FAQItem = ({ question, answer, icon, delay, isFirst = false }) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <MotionBox
      w="100%"
      bg={COLORS.cardBackground}
      borderRadius="lg"
      overflow="hidden"
      boxShadow="md"
      border="1px solid"
      borderColor={isOpen ? COLORS.primary : "transparent"}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{
        boxShadow: `0 0 15px ${COLORS.primary}33`,
        borderColor: COLORS.primary,
      }}
    >
      <Flex
        p={{ base: 3, md: 4 }}
        align="center"
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        bg={isOpen ? `${COLORS.primary}15` : "transparent"}
        transition="background 0.3s ease"
        borderRadius="lg"
      >
        <Flex align="center" width="100%" justifyContent="center">
          <Box
            bg={COLORS.primary}
            color="white"
            p={{ base: 1.5, md: 2 }}
            borderRadius="full"
            mr={{ base: 2, md: 3 }}
            transition="transform 0.3s ease"
            transform={isOpen ? "scale(1.1)" : "scale(1)"}
          >
            <Icon as={icon} boxSize={{ base: 3, md: 4 }} />
          </Box>
          <Text
            fontWeight="600"
            fontSize={isFirst ? { base: "lg", md: "xl" } : { base: "md", md: "lg" }}
            color={COLORS.textPrimary}
            textAlign="center"
            flex="1"
          >
            {question}
          </Text>
          <Box>
            <Icon
              as={isOpen ? FaChevronUp : FaChevronDown}
              boxSize={{ base: 3, md: 4 }}
              color={COLORS.primary}
              transition="transform 0.3s ease"
              transform={isOpen ? "translateY(-2px)" : "translateY(2px)"}
            />
          </Box>
        </Flex>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Box
          p={{ base: 4, md: 6 }}
          color={COLORS.textPrimary}
          bg="white"
          borderTop="1px solid"
          borderColor={`${COLORS.primary}30`}
          boxShadow="inner"
        >
          <MotionText
            whiteSpace="pre-line"
            textAlign="center"
            fontSize={{ base: "sm", md: "md" }}
            lineHeight="1.8"
            letterSpacing="0.2px"
            maxW="90%"
            mx="auto"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            sx={{
              "& p": {
                marginBottom: "1em",
              },
            }}
          >
            {answer}
          </MotionText>
        </Box>
      </Collapse>
    </MotionBox>
  );
};

function getIconForQuestion(index) {
  const icons = [
    FaQuestion, // Comment fonctionne
    FaLightbulb, // Différence entre modèles
    FaFlask, // Combien de recettes
    FaClock, // Temps pour servir
    FaGlobe, // Suivre à distance
    FaTint, // Alimentation en eau
    FaCreditCard, // Moyens de paiement
    FaSprayCan, // Nettoyage
    FaTools, // Maintenance
  ];

  return icons[index % icons.length];
}

export default FAQ;
